/**
 * ============================================================================
 * [P3-05-D] RestoreConfirm - 복원 확인 모달 컴포넌트
 *
 * 기능:
 * - 백업 파일 미리보기
 * - 복원 확인/취소
 * - 파일 유효성 검사
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useEffect } from 'react'
import styles from './Backup.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface BackupPreview {
  version: string
  createdAt: string
  textsCount: number
  trackingPostsCount: number
  urlLinksCount: number
  scriptsCount: number
}

interface RestoreConfirmProps {
  file: File
  onConfirm: () => void
  onCancel: () => void
  isProcessing?: boolean
}

// ============================================================================
// 3. RestoreConfirm Component
// ============================================================================
export function RestoreConfirm({ file, onConfirm, onCancel, isProcessing = false }: RestoreConfirmProps) {
  // ========================================================================
  // 3-1. 상태
  // ========================================================================
  const [preview, setPreview] = useState<BackupPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // ========================================================================
  // 3-2. ESC 키로 모달 닫기
  // ========================================================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessing) {
        onCancel()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onCancel, isProcessing])

  // ========================================================================
  // 3-3. 파일 파싱
  // ========================================================================
  useEffect(() => {
    const parseFile = async () => {
      setIsLoading(true)
      try {
        const content = await file.text()
        const data = JSON.parse(content)

        // 레거시 포맷 감지: data 래퍼 없이 최상위에 texts가 있는 경우
        const isLegacyFormat = !data.data && Array.isArray(data.texts)
        const dataSection = isLegacyFormat
          ? { texts: data.texts, trackingPosts: data.trackingPosts || [], urlLinks: data.urlLinks || [], scripts: [] }
          : data.data

        // 유효성 검사
        if (!dataSection || typeof dataSection !== 'object') {
          throw new Error('유효하지 않은 백업 파일입니다. (데이터 없음)')
        }

        const hasAnyData = (dataSection.texts?.length || 0) > 0
          || (dataSection.trackingPosts?.length || 0) > 0
          || (dataSection.urlLinks?.length || 0) > 0
          || (dataSection.scripts?.length || 0) > 0

        if (!hasAnyData) {
          throw new Error('백업 파일에 복원할 데이터가 없습니다.')
        }

        // 기존 js/backup.js 포맷과 호환 (texts, trackingPosts, urlLinks, scripts)
        setPreview({
          version: data.version || (isLegacyFormat ? 'legacy' : '알 수 없음'),
          createdAt: data.createdAt || data.exportedAt || '알 수 없음',
          textsCount: dataSection.texts?.length || 0,
          trackingPostsCount: dataSection.trackingPosts?.length || 0,
          urlLinksCount: dataSection.urlLinks?.length || dataSection.urls?.length || 0,
          scriptsCount: dataSection.scripts?.length || 0,
        })
        setError(null)
      } catch (err) {
        if (err instanceof SyntaxError) {
          setError('JSON 파싱 실패: 올바른 백업 파일이 아닙니다.')
        } else {
          setError(err instanceof Error ? err.message : '파일 파싱 실패')
        }
        setPreview(null)
      } finally {
        setIsLoading(false)
      }
    }

    parseFile()
  }, [file])

  // ========================================================================
  // 3-4. 날짜 포맷팅
  // ========================================================================
  const formatCreatedAt = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('ko-KR')
    } catch {
      return dateStr
    }
  }

  // ========================================================================
  // 3-5. Render
  // ========================================================================
  return (
    <div
      className={styles.modalBackdrop}
      onClick={isProcessing ? undefined : onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-title"
    >
      <div
        className={styles.restoreModal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================================================================
            제목
            ================================================================ */}
        <h3 id="restore-title">백업 복원 확인</h3>

        {/* ================================================================
            로딩 상태
            ================================================================ */}
        {isLoading && (
          <div className={styles.loadingState}>
            <span className={styles.spinner} />
            <p>파일 분석 중...</p>
          </div>
        )}

        {/* ================================================================
            에러 상태
            ================================================================ */}
        {!isLoading && error && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>❌</span>
            <p>{error}</p>
          </div>
        )}

        {/* ================================================================
            미리보기 정보
            ================================================================ */}
        {!isLoading && preview && (
          <div className={styles.previewInfo}>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>파일:</span>
              <span className={styles.previewValue}>{file.name}</span>
            </div>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>버전:</span>
              <span className={styles.previewValue}>{preview.version}</span>
            </div>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>생성일:</span>
              <span className={styles.previewValue}>
                {formatCreatedAt(preview.createdAt)}
              </span>
            </div>

            <hr className={styles.divider} />

            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>저장된 글:</span>
              <span className={styles.previewValue}>{preview.textsCount}개</span>
            </div>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>트래킹 포스트:</span>
              <span className={styles.previewValue}>{preview.trackingPostsCount}개</span>
            </div>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>URL 링크:</span>
              <span className={styles.previewValue}>{preview.urlLinksCount}개</span>
            </div>
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>스크립트:</span>
              <span className={styles.previewValue}>{preview.scriptsCount}개</span>
            </div>

            {/* 경고 메시지 */}
            <div className={styles.warningMessage}>
              <span className={styles.warningIcon}>⚠️</span>
              <span>복원하면 현재 데이터가 덮어씌워집니다.</span>
            </div>
          </div>
        )}

        {/* ================================================================
            버튼 영역
            ================================================================ */}
        <div className={styles.modalActions}>
          <button onClick={onCancel} disabled={isProcessing}>
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={!preview || !!error || isLoading || isProcessing}
            className={styles.dangerBtn}
          >
            {isProcessing ? '복원 중...' : '복원 실행'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RestoreConfirm
